/**
 * Shared helpers for the storefront live form field wrappers —
 * validation rules (consumed by react-hook-form) and type maps.
 */

import type { RegisterOptions } from 'react-hook-form';

export const PATTERNS: Record<string, { value: RegExp; message: string }> = {
  email: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' },
  url: { value: /^https?:\/\/.+/, message: 'Please enter a valid URL (https://…)' },
  phone: { value: /^[+\d\s\-().]{7,20}$/, message: 'Please enter a valid phone number' },
};

/** HTML input type for a form field type. */
export function inputTypeOf(fieldType?: string): string {
  switch (fieldType) {
    case 'email': return 'email';
    case 'phone': return 'tel';
    case 'number': return 'number';
    case 'url': return 'url';
    default: return 'text';
  }
}

/** react-hook-form register options built from the designed field props. */
export function buildValidationRules(props: {
  fieldType?: string;
  required?: boolean;
  requiredMessage?: string;
  minLength?: number;
  maxLength?: number;
  label: string;
}): RegisterOptions {
  const rules: RegisterOptions = {};
  if (props.required) {
    rules.required = props.requiredMessage?.trim()
      ? props.requiredMessage
      : `${props.label} is required`;
  }
  if (props.minLength && props.minLength > 0) {
    rules.minLength = { value: props.minLength, message: `Must be at least ${props.minLength} characters` };
  }
  if (props.maxLength && props.maxLength > 0) {
    rules.maxLength = { value: props.maxLength, message: `Must be at most ${props.maxLength} characters` };
  }
  const pattern = props.fieldType ? PATTERNS[props.fieldType] : undefined;
  if (pattern) rules.pattern = pattern;
  return rules;
}

/** Unique, stable element ids for label/input/error wiring (a11y). */
export function fieldIds(puckId?: string) {
  const base = `fe-${puckId || Math.random().toString(36).slice(2, 8)}`;
  return { input: base, error: `${base}-error`, help: `${base}-help` };
}
